import { useState, useEffect } from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { analyticsService } from '@/services/analyticsService'
import toast from 'react-hot-toast'

interface NewTrip {
  id: string
  title: string
  description: string
  createdBy: string
  createdAt: string
  startDate: string | null
  endDate: string | null
  isPublic: boolean
  isShared: boolean
  location: any
  participants: string[]
  tags: string[]
  status: string
  user: {
    id: string
    fullName: string
    email: string
    photoURL: string
  }
  totalParticipants: number
}

interface ChartData {
  date: string
  fullDate: string
  count: number
}

export const NewTripsChart = () => {
  const [trips, setTrips] = useState<NewTrip[]>([])
  const [chartData, setChartData] = useState<ChartData[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPeriod, setSelectedPeriod] = useState('7d')
  const [summary, setSummary] = useState<any>(null)

  useEffect(() => {
    const fetchNewTrips = async () => {
      try {
        setLoading(true)
        const data = await analyticsService.getNewTrips(selectedPeriod)
        
        setTrips(data.trips)
        setSummary(data.summary)

        // Create chart data from trips
        const tripsByDate = new Map<string, number>()
        data.trips.forEach(trip => {
          const dateKey = new Date(trip.createdAt).toISOString().split('T')[0]
          tripsByDate.set(dateKey, (tripsByDate.get(dateKey) || 0) + 1)
        })

        // Generate chart data for all dates in range
        const chartDataArray: ChartData[] = []
        const startDate = new Date(data.summary.dateRange.start)
        const endDate = new Date(data.summary.dateRange.end)
        
        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
          const dateKey = d.toISOString().split('T')[0]
          chartDataArray.push({
            date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            fullDate: dateKey,
            count: tripsByDate.get(dateKey) || 0
          })
        }

        setChartData(chartDataArray)
      } catch (error: any) {
        console.error('Failed to fetch new trips:', error)
        const errorMessage = error.response?.data?.error || 'Failed to load new trips data'
        toast.error(errorMessage)
        setTrips([])
        setChartData([])
        setSummary(null)
      } finally {
        setLoading(false)
      }
    }

    fetchNewTrips()
  }, [selectedPeriod])

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">New Trips</h3>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1"
          >
            <option value="7d">7d</option>
            <option value="30d">30d</option>
            <option value="90d">90d</option>
          </select>
        </div>
        <div className="h-48 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">New Trips</h3>
          {summary && (
            <div className="flex items-center mt-1">
              <span className="text-2xl font-bold text-gray-900">
                {summary.totalTrips}
              </span>
              <span className="ml-2 text-sm font-medium text-gray-500">
                avg {summary.averageTripsPerDay}/day
              </span>
            </div>
          )}
        </div>
        <select
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          className="text-sm border border-gray-300 rounded-md px-2 py-1"
        >
          <option value="7d">7d</option>
          <option value="30d">30d</option>
          <option value="90d">90d</option>
        </select>
      </div>

      {chartData.length > 0 ? (
        <div className="h-48 mb-6">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                label={{ value: 'Date', position: 'insideBottom', offset: -5, style: { fontSize: 12 } }}
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                axisLine={false}
                tickLine={false}
                label={{ value: 'Count', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
              />
              <Tooltip 
                formatter={(value: number) => [value.toLocaleString(), 'New Trips']}
                labelFormatter={(label, payload) => {
                  if (payload && payload[0] && payload[0].payload.fullDate) {
                    const date = new Date(payload[0].payload.fullDate)
                    if (!isNaN(date.getTime())) {
                      return date.toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })
                    }
                  }
                  return label
                }}
                labelStyle={{ color: '#374151' }}
              />
              <Area 
                type="monotone" 
                dataKey="count" 
                stroke="#8b5cf6" 
                fill="#8b5cf6"
                fillOpacity={0.1}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-48 flex items-center justify-center text-gray-500 mb-6">
          No new trips data available
        </div>
      )}

      {/* Recent Trips List */}
      {trips.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Trips</h4>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {trips.map((trip) => (
              <div key={trip.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-lg bg-purple-300 flex items-center justify-center">
                      <span className="text-sm font-medium text-purple-600">
                        🗺️
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {trip.title}
                    </p>
                    <div className="flex items-center space-x-2 mt-1">
                      <p className="text-xs text-gray-500 truncate">
                        by {trip.user.fullName}
                      </p>
                      {trip.isPublic && (
                        <span className="text-xs bg-green-100 text-green-800 px-1 rounded">
                          Public
                        </span>
                      )}
                      <span className={`text-xs px-1 rounded ${
                        trip.status === 'planning' ? 'bg-yellow-100 text-yellow-800' :
                        trip.status === 'active' ? 'bg-blue-100 text-blue-800' :
                        trip.status === 'completed' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {trip.status}
                      </span>
                      <span className="text-xs bg-purple-100 text-purple-800 px-1 rounded">
                        {trip.totalParticipants} participants
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-xs text-gray-500">
                    {new Date(trip.createdAt).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
} 